import { t } from "i18next";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useAppContext } from "qpr-base/context/AppCtxProvider";
import useUsermanagement, {
	type Group,
} from "qpr-base/apihooks/useUsermanagement";
import useSearchObjects from "backend/hooks/elements/useSearchObjects";
import useCreateAction from "backend/hooks/actions/useCreateAction";
import { makeThemedStyles } from "qpr-base/viewcomponents/utils";
import { Form, Formik, FormikProps } from "formik";
import * as Yup from "yup";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	IconButton,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import AddIcon from "@material-ui/icons/Add";

interface ActionDialogProps {
	open: boolean;
	onClose: () => void;
	kpiOrInitiative: {
		id: string;
		typename: "KPI" | "Initiative";
		name: string;
	} | null;
	isDisabled?: boolean;
}

interface ActionValues {
	actionName: string;
	deadline: string;
	description: string;
	assignTo: Group[];
	searchKpiInitiative: {
		id: string;
		typename: "KPI" | "Initiative";
		name: string;
		displayName: string;
	} | null;
	status: "Not Started" | "Completed" | "In Progress" | "Delayed";
}

interface UserOrGroup {
	id: string;
	name: string;
	pretty_name?: string;
	fullname?: string;
}

const shouldHideError = (error: unknown) => {
	if (!error) return false;
	const message = error instanceof Error ? error.message : String(error);
	return message.includes("Request failed with status code 404");
};

const useStyles = makeThemedStyles("ActionDialog", () => ({
	dialogPaper: {
		maxWidth: "700px",
		width: "90%",
		maxHeight: "90vh",
		margin: "40px auto",
		borderRadius: "8px",
	},
	dialogTitle: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		padding: "16px 24px",
		borderBottom: "1px solid #e0e0e0",
	},
	dialogContent: {
		padding: "24px",
		overflow: "auto",
		maxHeight: "calc(90vh - 100px)",
	},
	scrollContent: {
		overflow: "auto",
		padding: "10px",
		boxSizing: "border-box",
	},
	form: {
		maxWidth: "100%",
		width: "100%",
		boxSizing: "border-box",
	},
	field: {
		marginBottom: "16px",
	},
	label: {
		display: "block",
		marginBottom: "4px",
		fontWeight: 500,
	},
	input: {
		width: "100%",
		maxWidth: "100%",
		padding: "8px 12px",
		border: "1px solid #ccc",
		borderRadius: "4px",
		fontSize: "1rem",
		boxSizing: "border-box",
	},
	inputReadonly: {
		width: "100%",
		maxWidth: "100%",
		padding: "8px 12px",
		border: "1px solid #e0e0e0",
		borderRadius: "4px",
		fontSize: "1rem",
		boxSizing: "border-box",
		backgroundColor: "#f5f5f5",
		color: "#666",
	},
	textarea: {
		width: "100%",
		maxWidth: "100%",
		padding: "8px 12px",
		border: "1px solid #ccc",
		borderRadius: "4px",
		fontSize: "1rem",
		boxSizing: "border-box",
		minHeight: "80px",
		resize: "vertical",
		fontFamily: "inherit",
	},
	groupsDropdown: {
		width: "100%",
		marginTop: "4px",
	},
	fieldHint: {
		display: "block",
		marginTop: "4px",
		fontSize: "0.75rem",
		color: "#6b7280",
	},
	actions: {
		display: "flex",
		flexWrap: "wrap",
		gap: "12px",
		marginTop: "24px",
	},
	button: {
		padding: "10px 20px",
		cursor: "pointer",
		border: "1px solid #ccc",
		borderRadius: "4px",
		backgroundColor: "#fff",
		fontSize: "1rem",
		fontWeight: 500,
	},
	buttonPrimary: {
		backgroundColor: "#1976d2",
		color: "#fff",
		borderColor: "#1976d2",
	},
	buttonSecondary: {
		backgroundColor: "#f5f5f5",
		color: "#333",
		borderColor: "#ccc",
	},
	error: {
		color: "#d32f2f",
		marginBottom: "16px",
	},
	successMessage: {
		color: "#2e7d32",
		marginBottom: "16px",
		fontWeight: 500,
	},
	scorecardLabel: {
		display: "flex",
		alignItems: "center",
		gap: "8px",
		marginBottom: "4px",
		fontWeight: 500,
	},
	scorecardBadge: {
		display: "inline-block",
		padding: "2px 8px",
		borderRadius: "12px",
		fontSize: "0.75rem",
		fontWeight: 600,
		textTransform: "uppercase",
	},
	scorecardKpi: {
		backgroundColor: "#e3f2fd",
		color: "#1976d2",
	},
	scorecardInitiative: {
		backgroundColor: "#e8f5e9",
		color: "#2e7d32",
	},
}));

const ValidationSchema = Yup.object().shape({
	actionName: Yup.string().required(t("Action name is required")),
	deadline: Yup.string().required(t("Deadline is required")),
	description: Yup.string(),
});

const ActionDialog: React.FC<ActionDialogProps> = ({
													   open,
													   onClose,
													   kpiOrInitiative,
													   isDisabled = false,
												   }) => {
	const history = useHistory();
	const location = useLocation();
	const ctx = useAppContext();
	const tenant = ctx.tenant || "tec";
	const model = ctx.modelId || ctx.model;

	const { fetchGroupsAndUsers } = useUsermanagement();
	const apiRef = useRef<{ fetchGroupsAndUsers: typeof fetchGroupsAndUsers }>({ fetchGroupsAndUsers });
	apiRef.current.fetchGroupsAndUsers = fetchGroupsAndUsers;

	const createActionMutation = useCreateAction();

	const { data: searchData, isLoading: isSearchLoading } = useSearchObjects([
		"KPI",
		"Initiative",
	]);

	const [users, setUsers] = useState<UserOrGroup[]>([]);
	const [kpisAndInitiatives, setKpisAndInitiatives] = useState<
		Array<{
			id: string;
			typename: "KPI" | "Initiative";
			name: string;
			displayName: string;
		}>
	>([]);
	const [loading, setLoading] = useState(true);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const classes = useStyles();

	const [successMessage, setSuccessMessage] = useState<string | null>(null);

	const getInitialValues = (): ActionValues => ({
		actionName: "",
		deadline: "",
		description: "",
		assignTo: [],
		searchKpiInitiative: kpiOrInitiative
			? {
				...kpiOrInitiative,
				displayName: `${kpiOrInitiative.name} - ${
					kpiOrInitiative.typename === "KPI" ? "KPI" : "Initiative"
				}`,
			}
			: null,
		status: "Not Started",
	});

	const [initialValues, setInitialValues] = useState<ActionValues>(
		getInitialValues()
	);

	useEffect(() => {
		setInitialValues(getInitialValues());
	}, [kpiOrInitiative]);

	const loadData = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const { fetchGroupsAndUsers: fetchGroups } = apiRef.current;
			const { groups: g, users: u } = await fetchGroups();

			const allOptions: UserOrGroup[] = [
				...(u || []).map((user: any) => ({
					id: user.id,
					name: user.fullname || user.name || "",
					fullname: user.fullname,
				})),
				...(g || []).map((group: Group) => ({
					id: group.id,
					name: group.pretty_name || group.name || "",
					pretty_name: group.pretty_name,
				})),
			];
			setUsers(allOptions);
		} catch (e) {
			if (!shouldHideError(e)) {
				setError(e instanceof Error ? e.message : String(e));
			}
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		loadData();
	}, [loadData]);

	useEffect(() => {
		if (searchData && Array.isArray(searchData)) {
			const transformed = searchData.map((item: any) => ({
				id: item.id,
				typename: item.typename as "KPI" | "Initiative",
				name: item.Name || item.name || "",
				displayName: `${item.Name || item.name || ""} - ${
					item.typename === "KPI" ? "KPI" : "Initiative"
				}`,
			}));
			setKpisAndInitiatives(transformed);
		}
	}, [searchData]);

	const filterOptions = (
		options: UserOrGroup[],
		state: { inputValue: string }
	) => {
		const query = (state.inputValue || "").trim().toLowerCase();
		if (!query) return options;
		return options.filter((item) => {
			const searchText = [item.name, item.fullname, item.pretty_name]
				.filter(Boolean)
				.join(" ")
				.toLowerCase();
			return searchText.includes(query);
		});
	};

	const handleSubmit = async (values: ActionValues) => {
		setSubmitting(true);
		setError(null);
		setSuccessMessage(null);

		try {
			const reqBody = {
				actionName: values.actionName,
				description: values.description,
				assignTo: values.assignTo.map((item) => item.id),
				assignToNames: values.assignTo.map(
					(item) => item.name || item.pretty_name || ""
				),
				searchKpiInitiativeId: kpiOrInitiative?.id || null,
				searchKpiInitiativeName: kpiOrInitiative?.name || null,
				searchKpiInitiativeType: kpiOrInitiative?.typename || null,
				status: t("Actions.Not Started"),
			};

			console.log("Submitting Action from Dialog:", reqBody);

			await createActionMutation.mutateAsync(reqBody);
			setSuccessMessage(t("Actions.Action created successfully"));

			setTimeout(() => {
				onClose();
			}, 1500);
		} catch (err) {
			if (!shouldHideError(err)) {
				setError(err instanceof Error ? err.message : String(err));
			}
		} finally {
			setSubmitting(false);
		}
	};

	const getScorecardBadgeClass = (): string => {
		if (!kpiOrInitiative) return "";
		return kpiOrInitiative.typename === "KPI"
			? classes.scorecardKpi
			: classes.scorecardInitiative;
	};

	const renderForm = () => (
		<div className={classes.scrollContent}>
			{error && (
				<div role="alert" className={classes.error}>
					{error}
				</div>
			)}

			{successMessage && (
				<div role="alert" className={classes.successMessage}>
					{successMessage}
				</div>
			)}

			<Formik
				initialValues={initialValues}
				validationSchema={ValidationSchema}
				enableReinitialize
				onSubmit={handleSubmit}
			>
				{({
					  handleSubmit: formikSubmit,
					  handleChange,
					  handleBlur,
					  values,
					  errors,
					  setFieldValue,
					  touched,
				  }: FormikProps<ActionValues>) => (
					<Form onSubmit={formikSubmit} className={classes.form}>
						<div className={classes.field}>
							<label className={classes.label} htmlFor="actionName">
								{t("Actions.Action Name")} *
							</label>
							<input
								id="actionName"
								name="actionName"
								type="text"
								className={classes.input}
								value={values.actionName}
								onChange={handleChange}
								onBlur={handleBlur}
								required
							/>
							{touched.actionName && errors.actionName && (
								<span className={classes.fieldHint} style={{ color: "#d32f2f" }}>
                  {errors.actionName}
                </span>
							)}
						</div>

						<div className={classes.field}>
							<label className={classes.label} htmlFor="deadline">
								{t("Actions.Deadline")} *
							</label>
							<input
								id="deadline"
								name="deadline"
								type="date"
								className={classes.input}
								value={values.deadline}
								onChange={handleChange}
								onBlur={handleBlur}
								required
							/>
							{touched.deadline && errors.deadline && (
								<span className={classes.fieldHint} style={{ color: "#d32f2f" }}>
                  {errors.deadline}
                </span>
							)}
						</div>

						<div className={classes.field}>
							<label className={classes.label} htmlFor="description">
								{t("Actions.Description")}
							</label>
							<textarea
								id="description"
								name="description"
								className={classes.textarea}
								value={values.description}
								onChange={handleChange}
								onBlur={handleBlur}
								placeholder={t("Actions.Enter action description")}
							/>
						</div>

						<div className={classes.field}>
							<div className={classes.scorecardLabel}>
								{t("Actions.Strategy Element")}
								{kpiOrInitiative && (
									<span
										className={`${classes.scorecardBadge} ${getScorecardBadgeClass()}`}
									>
                    {kpiOrInitiative.typename}
                  </span>
								)}
							</div>
							<input
								type="text"
								className={classes.inputReadonly}
								value={kpiOrInitiative?.name || ""}
								readOnly
								placeholder={t("No scorecard selected")}
							/>
							<span className={classes.fieldHint}>
                {kpiOrInitiative
					? kpiOrInitiative.typename === "KPI"
						? t("Actions.Linked from KPI")
						: t("Actions.Linked from Initiative")
					: t("Actions.Select a KPI or Initiative")}
              </span>
						</div>

						<div className={classes.actions}>
							<button
								type="submit"
								className={`${classes.button} ${classes.buttonPrimary}`}
								disabled={submitting}
							>
								{submitting ? t("Actions.Saving") : t("Actions.Create Action")}
							</button>
							<button
								type="button"
								className={`${classes.button} ${classes.buttonSecondary}`}
								onClick={onClose}
							>
								{t("Actions.Cancel")}
							</button>
						</div>
					</Form>
				)}
			</Formik>
		</div>
	);

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="md"
			fullWidth
			classes={{
				paper: classes.dialogPaper,
			}}
		>
			<DialogTitle className={classes.dialogTitle}>
        <span>
          {t("Actions.Create New Action")} -{" "}
			{kpiOrInitiative
				? kpiOrInitiative.typename === "KPI"
					? t("Actions.From KPI")
					: t("Actions.From Initiative")
				: t("Actions.New Action")}
        </span>
				<IconButton onClick={onClose} edge="end" color="inherit" aria-label="close">
					<CloseIcon />
				</IconButton>
			</DialogTitle>
			<DialogContent className={classes.dialogContent}>
				{renderForm()}
			</DialogContent>
		</Dialog>
	);
};

export default ActionDialog;

